import * as Sentry from '@sentry/browser';
import { auth } from '../../constants/firebaseModular';

/**
 * Create a Stripe PaymentIntent and return its client secret (CU-17tm466h6uv).
 *
 * WHY THIS SHAPE. The previous version had no timeout, threw an UNHANDLED
 * rejection on a non-JSON body, and on an error returned the string 'ERROR'
 * after an alert() — each of which left the self-pay page stuck on
 * "Connecting to Payment Provider..." with no way forward except a reload. A
 * patient waited ~1:40 on exactly that (MIDEO-UNIFIED-C2). This now either
 * returns a secret or THROWS a typed PaymentIntentError; the caller owns the UI.
 *
 * TIMEOUT + ONE RETRY. Each attempt is aborted after 30s. A cold
 * createPaymentIntent measured 19.9–28.2s on staging (2026-10-01), and this
 * codebase has measured a 124.5s cold start elsewhere, so the budget is
 * deliberately generous even though prod is now kept warm (CU-17tm466h6ut).
 * Retrying is SAFE: an abandoned PaymentIntent is never confirmed, so it never
 * charges, and the retry queues on the instance that is already booting.
 * Only timeout / network / 5xx retry — a 4xx will not change on a second try.
 */

export const PAYMENT_INTENT_ATTEMPT_TIMEOUT_MS = 30_000;
const MAX_ATTEMPTS = 2;

export type PaymentIntentErrorKind = 'timeout' | 'network' | 'server' | 'rejected' | 'malformed';

export class PaymentIntentError extends Error {
  readonly kind: PaymentIntentErrorKind;
  readonly status?: number;

  constructor(kind: PaymentIntentErrorKind, message: string, status?: number) {
    super(message);
    this.name = 'PaymentIntentError';
    this.kind = kind;
    this.status = status;
  }
}

const RETRYABLE: ReadonlySet<PaymentIntentErrorKind> = new Set(['timeout', 'network', 'server']);

/**
 * Sentry's default Dedupe integration drops an event identical to the PREVIOUS
 * one on message + fingerprint + stack — tags and extra are ignored. A patient
 * pressing Try Again into the same failure would therefore report once. The
 * stable fingerprint keeps every occurrence in ONE issue; the message gains a
 * sequence number from the second occurrence on so each is delivered.
 * Module-level on purpose: Dedupe's memory is page-wide. (Same approach as the
 * staging-only utils/mfa/sentryDedupe.ts, which this hotfix branch does not have.)
 */
let failureSequence = 0;

/** Exported for tests — module state would otherwise leak between them. */
export function __resetPaymentIntentSequenceForTest(): void {
  failureSequence = 0;
}

function reportFailure(error: PaymentIntentError): void {
  // The portal's Sentry transport has NO offline queue: an event captured
  // offline is created and then discarded. Skip rather than pretend.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
  failureSequence += 1;
  const base = `createPaymentIntent failed: ${error.kind}`;
  const message = failureSequence === 1 ? base : `${base} (#${failureSequence})`;
  Sentry.captureException(new Error(message), {
    fingerprint: ['payment-intent', error.kind],
    tags: { payment_intent_error: error.kind },
    extra: { status: error.status, detail: error.message },
  });
}

async function attempt(url: string): Promise<string> {
  let token: string | undefined;
  try {
    token = await auth.currentUser?.getIdToken();
  } catch (err) {
    throw new PaymentIntentError('network', `could not get ID token: ${(err as Error)?.message ?? String(err)}`);
  }

  const headers = new Headers();
  headers.append('Content-Type', 'application/json');
  if (token) headers.append('Authorization', `Bearer ${token}`);

  // The timer covers the body read as well as the request: a stalled body is
  // the same hang as a stalled request.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PAYMENT_INTENT_ATTEMPT_TIMEOUT_MS);
  try {
    let res: Response;
    try {
      res = await fetch(url, { method: 'GET', headers, signal: controller.signal });
    } catch (err) {
      if (controller.signal.aborted) {
        throw new PaymentIntentError('timeout', `no response within ${PAYMENT_INTENT_ATTEMPT_TIMEOUT_MS}ms`);
      }
      throw new PaymentIntentError('network', (err as Error)?.message ?? String(err));
    }

    let data: unknown;
    try {
      data = await res.json();
    } catch {
      if (controller.signal.aborted) {
        throw new PaymentIntentError('timeout', `response body not received within ${PAYMENT_INTENT_ATTEMPT_TIMEOUT_MS}ms`);
      }
      // A non-JSON 5xx (e.g. a gateway error page) is a server failure and worth
      // a retry; a non-JSON 2xx/4xx is a contract violation and is not.
      throw new PaymentIntentError(
        res.status >= 500 ? 'server' : 'malformed',
        `non-JSON response (HTTP ${res.status})`,
        res.status,
      );
    }

    const body = (data ?? {}) as { client_secret?: unknown; error?: { message?: string }; message?: string };
    if (res.status >= 500) {
      throw new PaymentIntentError('server', body.error?.message || body.message || `HTTP ${res.status}`, res.status);
    }
    if (res.status !== 200) {
      throw new PaymentIntentError('rejected', body.error?.message || body.message || `HTTP ${res.status}`, res.status);
    }
    if (typeof body.client_secret !== 'string' || body.client_secret.length === 0) {
      throw new PaymentIntentError('malformed', 'response has no client_secret', res.status);
    }
    return body.client_secret;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * @param amountCents  amount in cents (Stripe's unit)
 * @param receiptEmail optional; omitted from the request when empty rather than
 *                     sent as "" (whether Stripe accepts an empty string is unverified)
 */
export async function getPaymentIntent(amountCents: number, receiptEmail?: string): Promise<string> {
  const params = new URLSearchParams({ amount: String(amountCents) });
  if (receiptEmail) params.set('receipt_email', receiptEmail);
  const url = `${import.meta.env.VITE_CLOUDFUNCTIONS_URI}/createPaymentIntent?${params.toString()}`;

  let lastError: PaymentIntentError | undefined;
  for (let i = 0; i < MAX_ATTEMPTS; i += 1) {
    try {
      return await attempt(url);
    } catch (err) {
      lastError =
        err instanceof PaymentIntentError
          ? err
          : new PaymentIntentError('network', (err as Error)?.message ?? String(err));
      if (!RETRYABLE.has(lastError.kind)) break;
    }
  }

  const error = lastError ?? new PaymentIntentError('network', 'unknown failure');
  reportFailure(error);
  throw error;
}
